import { DateRangePicker } from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import sprite from "../../assets/icons.svg";
import { getLocalizedString } from "../../shared/translation";
import { useDispatch } from "react-redux";
import { setSelectedRange, setGlobalResetCount } from "../../reduxStore/features";
import { useEffect, useRef, useMemo } from "react";

export const QICustomDateRangePicker = ({
  selectedRange,
  setSelectedRangeLocal,
  localRange = false,
  localReport = false,
  resetCount,
  setResetCount,
  label,
  className,
  openCalendar = "right",
  warningMessage = "",
  showCustomLabel = false,
  singleDatePicker = false,
  showDefaultRanges = true,
  showReset = true,
  preventAutoUpdate = false, // New prop to prevent automatic date range updates
}) => {
  const dispatch = useDispatch();
  const lastDispatchedRef = useRef({ start: null, end: null });

  const dispatchIfChanged = (startISO, endISO) => {
    const prev = lastDispatchedRef.current;
    if (prev.start !== startISO || prev.end !== endISO) {
      dispatch(
        setSelectedRange({
          startDate: startISO,
          endDate: endISO,
        })
      );
      lastDispatchedRef.current = { start: startISO, end: endISO };
    }
  };

  // Memoize baseTime so it only changes when the minute actually changes,
  // preventing useEffect re-triggers on every render
  const baseTimeRef = useRef(moment().startOf('minute'));
  const currentMinute = moment().startOf('minute').valueOf();
  if (currentMinute !== baseTimeRef.current.valueOf()) {
    baseTimeRef.current = moment(currentMinute);
  }
  const baseTime = baseTimeRef.current;

  const dateRangeOptions = useMemo(() => {
    const options = {};
    options[getLocalizedString("last_one_hour", "Last 1 Hour")] = [
      moment(baseTime).subtract(1, "hour"),
      moment(baseTime),
    ];
    options[getLocalizedString("last_two_hours", "Last 2 Hours")] = [
      moment(baseTime).subtract(2, "hours"),
      moment(baseTime),
    ];
    options[getLocalizedString("last_six_hours", "Last 6 Hours")] = [
      moment(baseTime).subtract(6, "hours"),
      moment(baseTime),
    ];
    options[getLocalizedString("last_twelve_hours", "Last 12 Hours")] = [
      moment(baseTime).subtract(12, "hours"),
      moment(baseTime),
    ];
    options[getLocalizedString("last_twentyfour_hours", "Last 24 Hours")] = [
      moment(baseTime).subtract(24, "hours"),
      moment(baseTime),
    ];
    return options;
  }, [baseTime]);

  const AbsoluteValue = Math.abs(moment(selectedRange?.startDate).diff(moment(), "hours"));
  const rangeDurationHours = moment(selectedRange?.endDate).diff(moment(selectedRange?.startDate), 'hours', true);
  const hourText = AbsoluteValue > 1 ? "Hours" : "Hour";
  const dateLabel = singleDatePicker
    ? moment(selectedRange?.startDate).format("ddd D MMM hh:mm:ss A")
    : showCustomLabel && AbsoluteValue <= 24 && AbsoluteValue >= 0
    ? `Since ${moment(selectedRange?.startDate).format("DD/MM/YY - hh:mm:ss A")}`
    : `${moment(selectedRange?.startDate).format("ddd D MMM hh:mm:ss A")} - ${moment(
        selectedRange?.endDate
      ).format("ddd D MMM hh:mm:ss A")}`;

  const prevResetCountRef = useRef(resetCount);

  useEffect(() => {
    if (prevResetCountRef.current === resetCount) {
      return;
    }
    prevResetCountRef.current = resetCount;

    // Skip auto-update if preventAutoUpdate is enabled
    if (preventAutoUpdate) return;

    if (selectedRange) {
      const currentRangeDurationHours = moment(selectedRange?.endDate).diff(moment(selectedRange?.startDate), 'hours', true);
      const endTimeDiffFromNow = Math.abs(moment(selectedRange?.endDate).diff(moment(), 'minutes'));
      const startTimeMinutes = moment(selectedRange?.startDate).minutes();
      const endTimeMinutes = moment(selectedRange?.endDate).minutes();

      // Check if range matches predefined patterns: common duration, ends near now, aligned time boundaries
      const commonDurations = [1, 2, 6, 12, 24];
      const isDurationPredefined = commonDurations.includes(Math.round(currentRangeDurationHours));
      const isEndTimeNearNow = endTimeDiffFromNow <= 5;
      const isOnHourBoundaries = (startTimeMinutes === 0 && endTimeMinutes === 0) ||
                                 (startTimeMinutes === endTimeMinutes);

      if (isDurationPredefined && isEndTimeNearNow && isOnHourBoundaries) {
        const durationHours = Math.round(currentRangeDurationHours);
        const startDate = moment(baseTime).subtract(durationHours, "hours");
        const endDate = moment(baseTime);

        !localRange &&
          !localReport &&
          dispatchIfChanged(startDate.toISOString(), endDate.toISOString());

        if (localRange && setSelectedRangeLocal) {
          setSelectedRangeLocal({
            startDate: startDate,
            endDate: endDate,
          });
        }
      }
    }
  }, [resetCount, baseTime, preventAutoUpdate]);

  useEffect(() => {
    if (selectedRange) {
      if (!localRange && !localReport && setSelectedRangeLocal) {
        dispatchIfChanged(
          moment(selectedRange.startDate).toISOString(),
          moment(selectedRange.endDate).toISOString()
        );
      } else if (localRange && setSelectedRangeLocal) {
        setSelectedRangeLocal({
          startDate: moment(selectedRange.startDate),
          endDate: moment(selectedRange.endDate),
        });
      }
    }
  }, [localRange]);

  const prevShowCustomLabelRef = useRef(showCustomLabel);
  useEffect(() => {
    // Only reset to 24h when showCustomLabel transitions from false to true,
    // not on every baseTime change (which would override user selections)
    if (showCustomLabel && !prevShowCustomLabelRef.current) {
      const now = moment().startOf('minute');
      const startDate = moment(now).subtract(24, "hours");
      const endDate = now;

      if (!localRange && !localReport) {
        dispatchIfChanged(startDate.toISOString(), endDate.toISOString());
      } else if (setSelectedRangeLocal) {
        setSelectedRangeLocal({
          startDate: startDate,
          endDate: endDate,
        });
      }
    }
    prevShowCustomLabelRef.current = showCustomLabel;
  }, [showCustomLabel]);

  const handleDateRangeChange = (event, picker) => {
    const startDate = moment(picker.startDate).startOf('minute');
    const endDate = moment(picker.endDate).startOf('minute');

    !localRange &&
      !localReport &&
      dispatchIfChanged(startDate.toISOString(), endDate.toISOString());

    localRange &&
      setSelectedRangeLocal({
        startDate,
        endDate,
      });

    localReport &&
      setSelectedRangeLocal({
        startDate,
        endDate,
      });
  };

  const handleReset = () => {
    localRange && setResetCount((prev) => prev + 1);
    localReport && setResetCount((prev) => prev + 1);
    !localRange && !localReport && dispatch(setGlobalResetCount(resetCount + 1));

    const now = moment().startOf('minute');

    !localRange &&
      !localReport &&
      dispatchIfChanged(moment(now).subtract(24, "hours").toISOString(), now.toISOString());

    localRange &&
      setSelectedRangeLocal({
        startDate: moment(now).subtract(1, "hours"),
        endDate: now,
      });

    localReport &&
      setSelectedRangeLocal({
        startDate: picker.startDate,
        endDate: picker.endDate,
      });
  };

  const isFutureDate = (date) => {
    return date.isAfter(moment().add(24, "hours"));
  };

  return (
    <div className={`track-points-datepicker-wrapper ${className}`}>
      <div className="track-points-timerange qi-form-element ">
        {label && <label className="qi-input_label">{label}</label>}
        <DateRangePicker
          key={`${resetCount}-${selectedRange?.startDate}-${selectedRange?.endDate}`}
          initialSettings={{
            timePicker: true,
            singleDatePicker: singleDatePicker,
            startDate: moment(selectedRange?.startDate),
            endDate: moment(selectedRange?.endDate),
            ...(showDefaultRanges ? { ranges: dateRangeOptions } : {}),
            opens: openCalendar,
            drops: "auto",
            isInvalidDate: isFutureDate,
            autoUpdateInput: false,
          }}
          onApply={handleDateRangeChange}
        >
          <div className="qi-input_wrapper">
            <div id="reportrange" className="date-range">
              <span>
                <span className="calendar-icon">
                  <svg className={`icon`}>
                    <use href={`${sprite}#calendar`}></use>
                  </svg>
                </span>
                {dateLabel}
              </span>

              {showReset && AbsoluteValue > 24 && (
                <span>
                  <span className="icon-wrapper clickable" onClick={handleReset}>
                    <svg className={`icon`}>
                      <use href={`${sprite}#cross`}></use>
                    </svg>
                  </span>
                </span>
              )}
            </div>
            {warningMessage.length > 0 && (
              <div className="qi-input_warning ">
                <p className="qi-input_warning_message">{warningMessage}</p>
              </div>
            )}
          </div>
        </DateRangePicker>
      </div>
    </div>
  );
};
